import React, { Suspense, useEffect } from 'react';
import { Button } from '@aws-amplify/ui-react';
import FlexFormContainer from '../common/FlexFormContainer';
import { useForm, useFieldArray } from 'react-hook-form';
import EmergencyContactFormContainer from './containers/EmergencyContactFormContainer';
import ProfileContactFormContainer from './containers/ProfileContactFormContainer';
import { useProfileContactQuery } from './api/useProfileContactQuery';
import {
  useProfileContactPostMutation,
  useProfileContactPutMutation,
} from "./api/useProfileContactMutation";
import { useEmergencyContactsQuery } from "./api/useEmergencyContactsQuery";
import {
  useEmergencyContactPostMutation,
  useEmergencyContactDeleteMutation,
  useEmergencyContactPutMutation
} from './api/useEmergencyContactMutation';
import { useUserQuery } from '../common/useUserQuery';
import { useNavigate } from 'react-router-dom';


const defaultContact = {
  address1: "",
  address2: "",
  city: "",
  state: "",
  zip: "",
  primaryDialCode: "",
  primaryPhone: "",
  secondaryPhone: "",
  workPhone: "",
  emergencyContacts: [],
};

export default function ContactPage() {
  const navigate = useNavigate();
  const { data: userQueryData } = useUserQuery();
  const {
    isPending,
    data: profileContact,
    isSuccess,
  } = useProfileContactQuery(userQueryData?.patientUserId);

  const { data: emergencyContacts } = useEmergencyContactsQuery(
    userQueryData?.patientUserId
  );

  const { mutate: profileContactPost } = useProfileContactPostMutation(
    userQueryData?.patientUserId
  );
  const { mutate: profileContactPut } = useProfileContactPutMutation();
  const { mutate: emergencyContactAdd } = useEmergencyContactPostMutation(
    userQueryData?.patientUserId
  );
  const { mutate: emergencyContactDelete } =
    useEmergencyContactDeleteMutation();
  const { mutate: emergencyContactUpdate } = useEmergencyContactPutMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    defaultValues: defaultContact,
    values: { ...profileContact, emergencyContacts: emergencyContacts ?? [] },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "emergencyContacts",
    keyName: "key",
  });

  useEffect(() => {
    if (profileContact != null && emergencyContacts != null) {
      reset({ ...profileContact, emergencyContacts: emergencyContacts ?? [] });
    }
  }, [profileContact, emergencyContacts, reset]);


  const onEmergencyContactDelete = (ecId) => {
    emergencyContactDelete(ecId);
  };

  const onSubmit = async (values) => {
    const pPhone = !values.primaryPhone
      ? null
      : `${values.primaryDialCode}-${values.primaryPhone}`;
    const sPhone = !values.secondaryPhone
      ? null
      : `${values.secondaryDialCode}-${values.secondaryPhone}`;
    const wPhone = !values.workPhone
      ? null
      : `${values.workDialCode}-${values.workPhone}`;
    const contactParams = {
      id: userQueryData?.patientUserId,
      address1: values.address1,
      address2: values.address2,
      city: values.city,
      state: values.state,
      zip: values.zip,
      primaryPhone: pPhone,
      secondaryPhone: sPhone,
      workPhone: wPhone,
    };

    if (profileContact && profileContact.id) {
      const dirtyPayload = JSON.parse(JSON.stringify(contactParams));
      profileContactPut(dirtyPayload);
    } else {
      await profileContactPost(contactParams);
    }

    for (let item of values.emergencyContacts) {
      const phoneFormat = !item.phone ? null : `${item.dialCode}-${item.phone}`;
      item = {
        ...item,
        phone: phoneFormat,
      };
      if (!item.id) {
        await emergencyContactAdd({
          profileId: userQueryData?.patientUserId,
          ...item,
        });
      } else {
        emergencyContactUpdate(item);
      }
    }

    reset();
    navigate("/user/quick-actions/profile-summary");
  };

  if (isPending) return "Loading...";


  if (isSuccess)
    return (
      <>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FlexFormContainer headerTitle={"Contact"}>
            <ProfileContactFormContainer
              control={control}
              profileContact={profileContact}
              errors={errors}
              setValue={setValue}
              getValues={getValues}
            />
            <Suspense fallback="Loading...">
              <EmergencyContactFormContainer
                control={control}
                append={append}
                remove={remove}
                fields={fields}
                onEmergencyContactDelete={onEmergencyContactDelete}
                errors={errors}
                getValues={getValues}
                setValue={setValue}
              />
            </Suspense>

            <Button variation="primary" type="submit">
              Save
            </Button>
          </FlexFormContainer>
        </form>
      </>
    );
}
